/**
 * Shared token interpolation for compose block params.
 *
 * Supports:
 * - {params.id}, {params.awardId}, etc. → values from the params object
 * - {meta.fieldName}                    → value from the meta bag (wizard context, route params, etc.)
 *
 * Unrecognized tokens are left as-is (returned as the raw match).
 */

const TOKEN_RE = /\{([^}]+)\}/g;

/** Coerce ControlRenderer route params to string tokens for fetch interpolation. */
export function normalizeBlockParams(
  params: Record<string, unknown> | undefined,
): Record<string, string> | undefined {
  if (!params) return undefined;
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      result[key] = String(value);
    }
  }
  return result;
}

export function interpolateBlockParams(
  template: Record<string, string>,
  params: Record<string, string> | undefined,
  meta: Record<string, unknown> | undefined,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(template)) {
    result[key] = interpolateBlockParam(value, params, meta);
  }
  return result;
}

export function interpolateBlockParam(
  template: string,
  params: Record<string, string> | undefined,
  meta: Record<string, unknown> | undefined,
): string {
  return template.replace(TOKEN_RE, (match, path: string) => {
    // {params.*} — primary token syntax
    if (path.startsWith('params.') && params) {
      const key = path.slice(7); // strip "params."
      return params[key] ?? '';
    }
    // {meta.*} — behavioral context
    if (path.startsWith('meta.') && meta) {
      const value = getNestedValue(meta, path.slice(5));
      return value != null ? String(value) : '';
    }
    return match;
  });
}

/**
 * Check if any param template references {meta.*} or {params.*} but would resolve to empty.
 * Used to decide whether to skip an API call when required context is missing.
 */
export function hasUnresolvedMetaParams(
  template: Record<string, string>,
  params: Record<string, string> | undefined,
  meta: Record<string, unknown> | undefined,
): boolean {
  return Object.values(template).some((value) => {
    if (!/\{(meta|params)\.[^}]+\}/.test(value)) return false;
    const resolved = interpolateBlockParam(value, params, meta);
    return resolved === '';
  });
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}
